/**
 * useChat — Transport-aware chat hook switcher.
 *
 * Picks between:
 *   - WebSocket (useAgentChat) when `localStorage.getItem('boox:chat-transport') === 'ws'`
 *   - SSE (useChatStream) — default, production-proven path
 *
 * Returns the same API shape regardless of transport.
 */

import { useChatStream } from './useChatStream';
import { useAgentChat } from './useAgentChat';

function isWsTransport(): boolean {
  try {
    const wsChatEnabled = import.meta.env?.VITE_ENABLE_AGENT_WS_CHAT === 'true';
    return wsChatEnabled && globalThis.localStorage?.getItem('boox:chat-transport') === 'ws';
  } catch {
    return false;
  }
}

export function useChat(convId: string | null) {
  if (isWsTransport()) return useAgentChat(convId);
  return useChatStream(convId);
}

// Re-export everything from useChatStream so ChatPage only needs one import source
export {
  CHAT_REPLY_LANGUAGE_OPTIONS,
  type ChatReplyLanguage,
  type ChatSession,
  type ChatMessage,
  type ChatMode,
  type ChatActionResult,
  type ThinkingStep,
  type AgentInfo,
  type AutonomousState,
  type AutonomousEvaluation,
  type ApprovalRecommendation,
  type LayerInvocationEnvelope,
  type ChatWidget,
} from './useChatStream';
